import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import { capitalizeFirstLetter } from "../../utils/helpers";
import moment from "moment";
import DemographicsData from "./DemographicsData";
import { formatTime } from "../../utils/CommonFunction";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const ExpandableIssueRow = ({ issue }) => {
  const [showFull, setShowFull] = useState(false);

  const truncate = (text) =>
    !text || text.length <= 30 ? text : `${text.substring(0, 30)}...`;

  return (
    <TableRow>
      <TableCell>{issue?.name || "N/A"}</TableCell>
      <TableCell>{issue?.email || "N/A"}</TableCell>
      <TableCell>
        {showFull ? issue?.subject : truncate(issue?.subject)}
        {issue?.subject?.length > 30 && (
          <Button
            size="small"
            onClick={() => setShowFull(!showFull)}
            sx={{ ml: 1, textTransform: "none" }}
          >
            {showFull ? "Show less" : "Show more"}
          </Button>
        )}
      </TableCell>
      <TableCell>
        {showFull ? issue?.description : truncate(issue?.description)}
        {issue?.description?.length > 30 && (
          <Button
            size="small"
            onClick={() => setShowFull(!showFull)}
            sx={{ ml: 1, textTransform: "none" }}
          >
            {showFull ? "Show less" : "Show more"}
          </Button>
        )}
      </TableCell>
      <TableCell>{issue?.queryStatus || "N/A"}</TableCell>
      <TableCell>
        {issue?.createdAt
          ? moment(issue.createdAt).format("YYYY-MM-DD HH:mm")
          : "N/A"}
      </TableCell>
    </TableRow>
  );
};

const formatMonthlyRevenueData = (data = []) => {
  return data.map((item) => {
    let label = "";

    if (item.year && item.month) {
      // Format A: use year + month
      const date = new Date(item.year, item.month - 1); // JS months are 0-based
      label = `${date.toLocaleString("default", { month: "short" })} ${item.year}`;
    } else if (item.date) {
      // Format B: parse date string
      const date = new Date(item.date);
      label = `${date.toLocaleString("default", { month: "short" })} ${date.getFullYear()}`;
    }

    return {
      name: label,
      totalSales: item.totalSales ?? 0,
      totalOrders: item.totalOrders ?? 0,
      avgOrderValue: item.avgOrderValue ?? 0,
    };
  });
};
const formatDailyRevenueData = (data = []) => {
  return data.map((item) => {
    const dateObj = new Date(item.date);
    const day = dateObj.getDate();
    const monthShort = dateObj.toLocaleString("default", { month: "short" }); // "Jul"
    return {
      ...item,
      name: `${day} ${monthShort}`, // Example: "3 Jul"
    };
  });
};



const ReportPopup = ({ open, onClose, reportType, reportData, fromDate, toDate }) => {
  let finalExportData = [];

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString();

  const exportSummaryToExcel = ({ data, fileName, sheetName }) => {
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Set column widths for better formatting
    worksheet['!cols'] = Array(12).fill({ wch: 20 });

    // Merge Dealer Info title (Row 0) and Order Details title (Row 3)
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 11 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 11 } }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || "Report");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${fileName}.xlsx`);
  };





  const renderTable = () => {
    if (!reportData || Object.keys(reportData).length === 0) {
      return <Typography>No data available.</Typography>;
    }

    switch (reportType) {
      case "demographics":
        return <DemographicsData data={reportData} />

      case "orders":
        return (
          <TableContainer component={Paper}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: reportData.map((order) => ({
                    "Order #": order.orderNumber,
                    User: order.userId,
                    Address: order.userAddress
                      ? `${order.userAddress.flatNumber}, ${order.userAddress.locality}, ${order.userAddress.city}, ${order.userAddress.state}, ${order.userAddress.pinCode}`
                      : "N/A",
                    Driver: order.driverDetails?.name || "N/A",
                    Status: order.orderStatus?.stage || "N/A",
                    "Total": order.total,
                    "Payment Method": order.paymentMethod,
                    "Payment Status": order.paymentStatus
                  })),
                  fileName: "OrderReport",
                  sheetName: "Orders"
                })
              }
              style={{ margin: "16px" }}
            >
              Export Excel
            </Button>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Order #</strong></TableCell>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell><strong>Driver</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell><strong>Payment</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(reportData) && reportData.length > 0 ? (
                  reportData.map((order, index) => (
                    <TableRow key={order._id || index}>
                      <TableCell>{order.orderNumber}</TableCell>
                      <TableCell>{order.userId}</TableCell>
                      <TableCell>
                        {order.userAddress
                          ? `${order.userAddress.flatNumber}, ${order.userAddress.locality}, ${order.userAddress.city}, ${order.userAddress.state}, ${order.userAddress.pinCode}`
                          : "N/A"}
                      </TableCell>
                      <TableCell>{order.driverDetails?.name || "N/A"}</TableCell>
                      <TableCell>{order.orderStatus?.stage || "N/A"}</TableCell>
                      <TableCell>{order.total}</TableCell>
                      <TableCell>{order.paymentMethod} ({order.paymentStatus})</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No orders available.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case "dealerPerformance":
        const groupedRows = reportData.allOrders?.map(order => {
          const productsSummary = order.products
            .map(p => `${p.name} - ${p.qty}`)
            .join(", ");

          const currency = order.products?.[0]?.currency || "USD";

          return {
            orderNumber: order.orderNumber,
            productsSummary,
            qty: order.products.reduce((acc, p) => acc + p.qty, 0),
            orderTotal: order.total,
            currency: currency,
            paymentMethod: order.paymentMethod,
            orderStatus: order.orderStatus?.stage,
            orderDate: new Date(order.createdAt).toLocaleDateString(),
            driverName: order.driverDetails?.name || "-",
            driverPhone: order.driverDetails?.phoneNumber || "-",
            commissionAmount: order.commissionDetails?.[0]?.amount || 0,
            commissionStatus: order.commissionDetails?.[0]?.status || "N/A",
            commissionDate: order.commissionDetails?.[0]
              ? new Date(order.commissionDetails[0].createdAt).toLocaleDateString()
              : "N/A"
          };
        }) || [];

        finalExportData = [
          { A: "Dealer Info" },
          {
            A: `Dealer Name: ${reportData.businessName}`,
            B: `Phone: ${reportData.countryCode}${reportData.phoneNumber}`,
            C: `Address: ${[
              reportData.houseNumber,
              reportData.streetNumber,
              reportData.village,
              reportData.commune,
              reportData.district,
              reportData.city,
              reportData.postalCode && `- ${reportData.postalCode}`
            ].filter(Boolean).join(", ")}`,
            D: `Total Sales: ${reportData.totalSales}`,
            E: `Total Orders: ${reportData.totalOrders}`,
            F: `Avg Order Value: ${reportData.avgOrderValue}`
          },
          {}, // empty row
          { A: "Order Details" },
          {
            A: "Order Number",
            B: "Products",
            C: "Total Qty",
            D: "Order Total",
            E: "Payment Method",
            F: "Order Status",
            G: "Order Date",
            H: "Driver Name",
            I: "Driver Phone",
            J: "Commission Amount",
            K: "Commission Status",
            L: "Commission Date"
          },
          ...groupedRows.map(row => ({
            A: row.orderNumber,
            B: row.productsSummary,
            C: row.qty,
            D: `${row.orderTotal} ${row.currency}`,
            E: row.paymentMethod,
            F: row.orderStatus,
            G: row.orderDate,
            H: row.driverName,
            I: row.driverPhone,
            J: row.commissionAmount,
            K: row.commissionStatus,
            L: row.commissionDate
          }))
        ];

        return (
          <TableContainer component={Paper} sx={{ p: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: finalExportData,
                  fileName: "DealerPerformanceReport",
                  sheetName: "Dealer Performance"
                })
              }
              sx={{ mb: 3 }}
            >
              Export Excel
            </Button>

            {/* Dealer Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6">Dealer Details</Typography>
              <Typography><strong>Name:</strong> {reportData.businessName}</Typography>
              <Typography><strong>Phone:</strong> {`${reportData.countryCode}${reportData.phoneNumber}`}</Typography>
              <Typography>
                <strong>Address:</strong>{" "}
                {[
                  reportData.houseNumber,
                  reportData.streetNumber,
                  reportData.village,
                  reportData.commune,
                  reportData.district,
                  reportData.city,
                  reportData.postalCode && `- ${reportData.postalCode}`
                ].filter(Boolean).join(", ")}
              </Typography>
              <Typography><strong>Total Sales:</strong> {reportData.totalSales}</Typography>
              <Typography><strong>Total Orders:</strong> {reportData.totalOrders}</Typography>
              <Typography><strong>Avg Order Value:</strong> {reportData.avgOrderValue}</Typography>
            </Box>

            {/* Order Table */}
            <Typography variant="h6" sx={{ mb: 1 }}>Order Details</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Order #</strong></TableCell>
                  <TableCell><strong>Products</strong></TableCell>
                  <TableCell><strong>Total Qty</strong></TableCell>
                  <TableCell><strong>Order Total</strong></TableCell>
                  <TableCell><strong>Payment</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Order Date</strong></TableCell>
                  <TableCell><strong>Driver Name</strong></TableCell>
                  <TableCell><strong>Driver Phone</strong></TableCell>
                  <TableCell><strong>Commission</strong></TableCell>
                  <TableCell><strong>Comm. Status</strong></TableCell>
                  <TableCell><strong>Comm. Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedRows.length > 0 ? (
                  groupedRows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.orderNumber}</TableCell>
                      <TableCell>{row.productsSummary}</TableCell>
                      <TableCell>{row.qty}</TableCell>
                      <TableCell>{`${row.orderTotal} ${row.currency}`}</TableCell>
                      <TableCell>{row.paymentMethod}</TableCell>
                      <TableCell>{row.orderStatus}</TableCell>
                      <TableCell>{row.orderDate}</TableCell>
                      <TableCell>{row.driverName}</TableCell>
                      <TableCell>{row.driverPhone}</TableCell>
                      <TableCell>{row.commissionAmount}</TableCell>
                      <TableCell>{row.commissionStatus}</TableCell>
                      <TableCell>{row.commissionDate}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
        );

      case "revenue":
        const dailyData = reportData.dailyRevenue || [];
        const monthlyData = (reportData.monthlyRevenue || []).map(item => ({
          name: `${item.year}-${String(item.month).padStart(2, '0')}`,
          totalSales: item.totalSales,
          totalOrders: item.totalOrders,
          avgOrderValue: item.avgOrderValue,
        }));

        return (
          <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Revenue Report</DialogTitle>
            <DialogContent dividers>
              {/* ✅ Daily Revenue Section */}
              {reportData?.dailyRevenue?.length > 0 && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Daily Revenue Summary (From {formatDate(fromDate)} to {formatDate(toDate)})
                  </Typography>

                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={formatDailyRevenueData(reportData.dailyRevenue)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="totalSales" stroke="#1976d2" name="Total Sales" />
                      <Line type="monotone" dataKey="totalOrders" stroke="#82ca9d" name="Total Orders" />
                      <Line type="monotone" dataKey="avgOrderValue" stroke="#8884d8" name="Avg Order Value" />
                    </LineChart>
                  </ResponsiveContainer>
                </>
              )}


              {/* ✅ Monthly Revenue Section */}
              {reportData?.monthlyRevenue?.length > 0 && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>
                    Monthly Revenue
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={formatMonthlyRevenueData(reportData.monthlyRevenue)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="totalSales" stroke="#1976d2" name="Total Sales" />
                      <Line type="monotone" dataKey="totalOrders" stroke="#82ca9d" name="Total Orders" />
                      <Line type="monotone" dataKey="avgOrderValue" stroke="#8884d8" name="Avg Order Value" />
                    </LineChart>
                  </ResponsiveContainer>


                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={onClose} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        );
      case "driverPerformance":
        const deliveryRows = reportData.allDeliveries?.map(order => {
          const productsSummary = order.products
            .map(p => `${p.name} - ${p.qty}`)
            .join(", ");
          const currency = order.products?.[0]?.currency || "USD";
          return {
            orderNumber: order.orderNumber,
            productsSummary,
            qty: order.products.reduce((acc, p) => acc + p.qty, 0),
            orderTotal: `${order.total} ${currency} `,
            paymentMethod: order.paymentMethod,
            orderStatus: order.orderStatus?.stage || "-",
            orderDate: new Date(order.createdAt).toLocaleDateString(),
            address: [
              order.userAddress?.houseNumber,
              order.userAddress?.streetNumber,
              order.userAddress?.village,
              order.userAddress?.commune,
              order.userAddress?.city,
              order.userAddress?.postalCode
            ]
              .filter(Boolean)
              .join(", ")
          };
        }) || [];

        const driverInfoRow = {
          A: `Name: ${reportData.name}`,
          B: `Phone: ${reportData.countryCode}${reportData.phoneNumber}`,
          B: `Email: ${reportData.email}`,
          C: `Address: ${reportData.address}`,
          D: `Total Deliveries: ${reportData.totalDeliveries}`,
        };

        finalExportData = [
          { A: "Driver Info" },
          driverInfoRow,
          {},
          { A: "Delivery Details" },
          {
            A: "Order Number",
            B: "Products",
            C: "Total Qty",
            D: "Order Total",
            E: "Payment Method",
            F: "Order Status",
            G: "Order Date",
            H: "Delivery Address"
          },
          ...deliveryRows.map(row => ({
            A: row.orderNumber,
            B: row.productsSummary,
            C: row.qty,
            D: row.orderTotal,
            E: row.paymentMethod,
            F: row.orderStatus,
            G: row.orderDate,
            H: row.address
          }))
        ];

        return (
          <TableContainer component={Paper}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: finalExportData,
                  fileName: "DriverPerformanceReport",
                  sheetName: "Driver Performance"
                })
              }
              sx={{ m: 2 }}
            >
              Export Excel
            </Button>

            {/* Driver Info Section */}
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">Driver Details</Typography>
              <Typography><strong>Name:</strong> {reportData.name}</Typography>
              <Typography><strong>Phone:</strong> {`${reportData.countryCode}${reportData.phoneNumber}`}</Typography>
              <Typography><strong>Email:</strong> {reportData.email}</Typography>
              <Typography><strong>Address:</strong> {reportData.address}</Typography>
              <Typography><strong>Total Deliveries:</strong> {reportData.totalDeliveries}</Typography>
            </Box>

            {/* Deliveries Table */}
            <Typography variant="h6" sx={{ px: 2 }}>Deliveries</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Order #</strong></TableCell>
                  <TableCell><strong>Products</strong></TableCell>
                  <TableCell><strong>Total Qty</strong></TableCell>
                  <TableCell><strong>Order Total</strong></TableCell>
                  <TableCell><strong>Payment</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Order Date</strong></TableCell>
                  <TableCell><strong>Delivery Address</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveryRows.length > 0 ? (
                  deliveryRows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.orderNumber}</TableCell>
                      <TableCell>{row.productsSummary}</TableCell>
                      <TableCell>{row.qty}</TableCell>
                      <TableCell>{row.orderTotal}</TableCell>
                      <TableCell>{row.paymentMethod}</TableCell>
                      <TableCell>{row.orderStatus}</TableCell>
                      <TableCell>{row.orderDate}</TableCell>
                      <TableCell>{row.address}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
        )
      case "financial":
        const formattedFinancialData = {
          "Total Orders": reportData?.totalOrders ?? 0,
          "Total Revenue": reportData?.totalRevenue?.toFixed(3) ?? "0.000"
        };

        return (
          <TableContainer component={Paper}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: [formattedFinancialData], // Wrap in array for XLSX
                  fileName: "FinancialReport",
                  sheetName: "Financial"
                })
              }
              style={{ margin: "16px" }}
            >
              Export Excel
            </Button>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Total Orders</strong></TableCell>
                  <TableCell><strong>Total Revenue</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{formattedFinancialData["Total Orders"]}</TableCell>
                  <TableCell>{formattedFinancialData["Total Revenue"]}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        );

      case "salesTrends":
        const formattedSalesTrends = reportData?.map((item) => ({
          "Product Name": item.productName || "N/A",
          "Total Quantity": item.totalQty ?? 0,
          "Total Sales": item.totalSales?.toFixed(2) ?? "0.00"
        }));

        return (
          <TableContainer component={Paper}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: formattedSalesTrends,
                  fileName: "SalesTrendsReport",
                  sheetName: "Sales Trends"
                })
              }
              style={{ margin: "16px" }}
            >
              Export Excel
            </Button>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Product Name</strong></TableCell>
                  <TableCell><strong>Total Quantity</strong></TableCell>
                  <TableCell><strong>Total Sales</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(reportData) && reportData.length > 0 ? (
                  reportData.map((item, index) => (
                    <TableRow key={item._id?.productId || index}>
                      <TableCell>{item.productName || "N/A"}</TableCell>
                      <TableCell>{item.totalQty ?? 0}</TableCell>
                      <TableCell>{item.totalSales?.toFixed(2) ?? "0.00"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No sales trends available.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case "refunds":
        return (
          <TableContainer component={Paper}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: reportData.allCancelledOrders.map((order) => ({
                    "Order No.": order?.orderNumber || "N/A",
                    "Date": order?.cancelDetails?.cancelDate
                      ? moment(order.cancelDetails.cancelDate).format("YYYY-MM-DD")
                      : "N/A",
                    "Reason": order?.cancelDetails?.cancelReason || "N/A",
                    "Status": order?.cancelDetails?.refundStatus || "N/A",
                    "Amount ": order?.cancelDetails?.refundedAmount ?? 0
                  })),
                  fileName: "RefundReport",
                  sheetName: "Refunds"
                })
              }
              style={{ margin: "16px" }}
            >
              Export Excel
            </Button>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Order No.</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Amount (USD)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(reportData.allCancelledOrders) &&
                  reportData.allCancelledOrders.length > 0 ? (
                  reportData.allCancelledOrders.map((order, index) => (
                    <TableRow key={order._id || index}>
                      <TableCell>{order?.orderNumber || "N/A"}</TableCell>
                      <TableCell>
                        {order?.cancelDetails?.cancelDate
                          ? moment(order.cancelDetails.cancelDate).format("YYYY-MM-DD")
                          : "N/A"}
                      </TableCell>
                      <TableCell>{order?.cancelDetails?.cancelReason || "N/A"}</TableCell>
                      <TableCell>{order?.cancelDetails?.refundStatus || "N/A"}</TableCell>
                      <TableCell>{order?.cancelDetails?.refundedAmount ?? 0}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case "orderReports":
        return (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: reportData.orders?.map((order) => ({
                    "Order Number": order?.orderNumber,
                    "User Name": order?.userInfo?.name || "-",
                    "User Email": order?.userInfo?.email || "-",
                    "Dealer Email": order?.dealerInfo?.email || "-",
                    "Dealer Phone": order?.dealerInfo?.phoneNumber || "-",
                    "Order Status": order?.orderStatus?.stage || "-",
                    "Driver Name": order?.driverDetails?.name || "-",
                    "Driver Phone": order?.driverDetails?.phoneNumber || "-",
                    "Total Amount": order?.total || 0,
                    "Payment Method": order?.paymentMethod || "-",
                    "Payment Status": order?.paymentStatus || "-",
                    "Schedule Date": order?.scheduleDate
                      ? moment(order.scheduleDate).format("YYYY-MM-DD")
                      : "-"
                  })),
                  fileName: "OrderReports",
                  sheetName: "Order Reports"
                })
              }
              style={{ margin: "16px" }}
            >
              Export Excel
            </Button>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order Number</TableCell>
                  <TableCell>User Name</TableCell>
                  <TableCell>User Email</TableCell>
                  <TableCell>Dealer Email</TableCell>
                  <TableCell>Dealer Phone</TableCell>
                  <TableCell>Order Status</TableCell>
                  <TableCell>Driver Name</TableCell>
                  <TableCell>Driver Phone</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Schedule Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(reportData.orders) && reportData.orders.length > 0 ? (
                  reportData.orders.map((order, index) => (
                    <TableRow key={order._id || index}>
                      <TableCell>{order?.orderNumber}</TableCell>
                      <TableCell>{order?.userInfo?.name || "-"}</TableCell>
                      <TableCell>{order?.userInfo?.email || "-"}</TableCell>
                      <TableCell>{order?.dealerInfo?.email || "-"}</TableCell>
                      <TableCell>{order?.dealerInfo?.phoneNumber || "-"}</TableCell>
                      <TableCell>{order?.orderStatus?.stage || "-"}</TableCell>
                      <TableCell>{order?.driverDetails?.name || "-"}</TableCell>
                      <TableCell>{order?.driverDetails?.phoneNumber || "-"}</TableCell>
                      <TableCell>{order?.total}</TableCell>
                      <TableCell>{order?.paymentMethod || "-"}</TableCell>
                      <TableCell>{order?.paymentStatus || "-"}</TableCell>
                      <TableCell>
                        {order?.scheduleDate
                          ? moment(order.scheduleDate).format("YYYY-MM-DD")
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        );

      case "userIssues": {
        return (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                exportSummaryToExcel({
                  data: reportData.map((issue) => ({
                    Name: issue?.name || "N/A",
                    Email: issue?.email || "N/A",
                    Subject:
                      issue?.subject?.length > 30000
                        ? issue.subject.slice(0, 30000) + "..."
                        : issue?.subject || "N/A",
                    Description:
                      issue?.description?.length > 30000
                        ? issue.description.slice(0, 30000) + "..."
                        : issue?.description || "N/A",
                    "Query Status": issue?.queryStatus || "N/A",
                    "Created At": issue?.createdAt
                      ? moment(issue.createdAt).format("YYYY-MM-DD HH:mm")
                      : "N/A"
                  })),

                  fileName: "UserIssues",
                  sheetName: "User Issues",
                })
              }
              style={{ margin: "16px" }}
            >
              Export Excel
            </Button>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Query Status</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(reportData) && reportData.length > 0 ? (
                  reportData.map((issue) => (
                    <ExpandableIssueRow key={issue?._id} issue={issue} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No issues available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        );
      }

      default:
        return <Typography>Invalid report type</Typography>;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {capitalizeFirstLetter(reportType).replace(/([A-Z])/g, " $1")} {reportType == "orderReports" ? "" : "Report"}
      </DialogTitle>
      <DialogContent dividers>{renderTable()}</DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportPopup;
